import css from './Valid.module.scss'
import React from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { QRCodeSVG } from 'qrcode.react'
import { GoodTicketCode } from 'types/tickets'

import DataTestId from 'meta/dataTestId'
import { IGoodServiceTicketData } from 'meta/pages/goodsandservices'
import { TICKETSMS_COLORS_WHITE } from 'utils/constants/colors'
import { Dates } from 'utils/dates'

import { ButtonRed } from 'components/Actions/Action'
import { useUrlCloudfront } from 'hooks/useEnv'

interface Props {
  data: IGoodServiceTicketData
  showTap2GoModal(show: boolean): void
  totalSelectedTickets?: number
  isTicketSelected?: boolean
  insideWallet?: boolean
}

const Valid: React.FC<Props> = (props) => {
  const { data, showTap2GoModal, totalSelectedTickets, isTicketSelected, insideWallet } = props

  const { t } = useTranslation()

  const urlCloudfront = useUrlCloudfront()

  const areGatesAlreadyOpen = Dates.isAfter(new Date(), new Date(data.openDateEvent_tz))

  const isMultipleSelectionActive = totalSelectedTickets !== undefined && totalSelectedTickets > 0

  const isTicketDrink = data?.event?.policy?.code == GoodTicketCode.drink

  return (
    <div data-testid={DataTestId.bag.ticketStatusValid} className={css.wrapper}>
      {data.hasVaultEnabled ? (
        <>
          <div className={css.qr}>
            <div className={css.scan_text}>
              {t(isTicketDrink ? 'goodsAndServices:scan_code_drink' : 'goodsAndServices:scan_code_generic')}
            </div>
            <div
              data-testid={DataTestId.bag.bgRectangularQR}
              className={css.qrCode_img}
              style={{ backgroundColor: data.customColor ?? TICKETSMS_COLORS_WHITE }}
            >
              <canvas
                data-testid={DataTestId.bag.canvasRectangularQR}
                ref={async (canvas) => {
                  if (!canvas) {
                    return
                  }

                  const bwipjs = await import('@bwip-js/browser')
                  bwipjs.toCanvas(canvas, {
                    bcid: 'datamatrixrectangular', // Barcode type
                    text: data.secret, // Text to encode
                    width: 35,
                    height: 15,
                  })
                }}
              />
            </div>
          </div>

          <div className={css.separator}>
            <span className={css.or}>{t('goodsAndServices:or')}</span>
          </div>

          <div className={css.use_drink}>
            <div className={css.use_drink_description}>{t('goodsAndServices:press_button')}</div>
            <ButtonRed
              data-testid={DataTestId.bag.btnUseTicket}
              className={classNames(css.use_drink_button, {
                [css.extraMarginBottom]: insideWallet && !data.webDetails,
              })}
              disabled={!areGatesAlreadyOpen || (isMultipleSelectionActive && !isTicketSelected)}
              onClick={() => {
                showTap2GoModal(true)
              }}
            >
              {`${t(isTicketDrink ? 'goodsAndServices:button_base_use_drink' : 'goodsAndServices:button_base_use_generic')} ${totalSelectedTickets && totalSelectedTickets > 0 ? `(${totalSelectedTickets})` : ``}`}
            </ButtonRed>
          </div>
        </>
      ) : (
        <>
          <div className={css.dataVaultDisabled}>
            <div
              className={css.qr}
              style={{
                borderRadius: '4px',
                padding: '8px',
                backgroundColor: data.customColor ?? TICKETSMS_COLORS_WHITE,
              }}
            >
              <QRCodeSVG
                value={data.secret}
                size={188}
                level="H"
                bgColor={data.customColor ?? TICKETSMS_COLORS_WHITE}
                imageSettings={{
                  src: `${urlCloudfront}/images/goodsandservices/qr-icon-${data.event.policy.code}.svg`,
                  width: 64,
                  height: 64,
                  excavate: true,
                }}
              />
            </div>
            <div className={css.scan_text}>
              {t(isTicketDrink ? 'goodsAndServices:scan_code_drink' : 'goodsAndServices:scan_code_generic')}
            </div>
          </div>
        </>
      )}

      <div className={css.bottom_label}>
        <div className={css.status}>
          {t(isTicketDrink ? `goodsAndServices:status_usable_drink` : `goodsAndServices:status_usable_generic`)}
        </div>
        <div className={css.datetime}>
          {areGatesAlreadyOpen
            ? Dates.format(new Date(data.validTo_tz), t('goodsAndServices:status_valid_datetime_to'))
            : Dates.format(new Date(data.validFrom_tz), t('goodsAndServices:status_valid_datetime'))}
        </div>
      </div>
    </div>
  )
}

export default Valid
